import {
	BE_MENU_INVOKE_MS,
	BeMenu,
	BeMenuBar,
	BeMenuBarItem,
	BeMenuItem,
	BeMenuSeparator,
	BeRow,
	BeScrollView,
	BeStack,
	BeWindow,
} from "@beos-css/react/index.ts";
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";

const SAMPLE = [
	"The Be Operating System is a modern operating system designed from the ground up for digital media.",
	"It is fully multithreaded, has protected memory, and a pervasive object-oriented API.",
	"The Media Kit moves audio and video between nodes in a directed graph, and every node runs on its own thread.",
	"The Storage Kit indexes file attributes, so a query is a live folder rather than a search that has to be re-run.",
	"The Interface Kit draws every control from the same eight-colour panel palette, tinted rather than themed.",
];

const SIZES = (
	<>
		{[9, 10, 12, 14, 18, 24].map((pt) => (
			<BeMenuItem key={pt} marked={pt === 12}>
				{pt}
			</BeMenuItem>
		))}
	</>
);

const COLORS = (
	<>
		{["Black", "Red", "Green", "Blue", "Yellow"].map((c) => (
			<BeMenuItem key={c} marked={c === "Black"}>
				{c}
			</BeMenuItem>
		))}
	</>
);

/** StyledEdit's own four menus, trimmed to what shows the kit off. */
const MENUS: Record<string, ReactNode> = {
	File: (
		<>
			<BeMenuItem shortcut={{ key: "N" }}>New</BeMenuItem>
			<BeMenuItem
				submenu={
					<>
						<BeMenuItem>Untitled 1</BeMenuItem>
						<BeMenuItem>Read Me</BeMenuItem>
						<BeMenuItem>Release Notes</BeMenuItem>
					</>
				}
			>
				Open…
			</BeMenuItem>
			<BeMenuItem shortcut={{ key: "S" }}>Save</BeMenuItem>
			<BeMenuItem shortcut={{ modifiers: ["shift", "command"], key: "S" }}>Save As…</BeMenuItem>
			<BeMenuItem disabled>Revert to Saved</BeMenuItem>
			<BeMenuSeparator />
			<BeMenuItem shortcut={{ key: "P" }}>Print…</BeMenuItem>
			<BeMenuSeparator />
			<BeMenuItem shortcut={{ key: "Q" }}>Quit</BeMenuItem>
		</>
	),
	Edit: (
		<>
			<BeMenuItem shortcut={{ key: "Z" }} disabled>
				Undo
			</BeMenuItem>
			<BeMenuSeparator />
			<BeMenuItem shortcut={{ key: "X" }}>Cut</BeMenuItem>
			<BeMenuItem shortcut={{ key: "C" }}>Copy</BeMenuItem>
			<BeMenuItem shortcut={{ key: "V" }}>Paste</BeMenuItem>
			<BeMenuSeparator />
			<BeMenuItem shortcut={{ key: "A" }}>Select All</BeMenuItem>
		</>
	),
	Font: (
		<>
			<BeMenuItem submenu={SIZES}>Size</BeMenuItem>
			<BeMenuItem submenu={COLORS}>Color</BeMenuItem>
			<BeMenuSeparator />
			<BeMenuItem marked>Swis721 BT</BeMenuItem>
			<BeMenuItem>Dutch801 Rm BT</BeMenuItem>
			<BeMenuItem>Courier10 BT</BeMenuItem>
		</>
	),
	Document: (
		<>
			<BeMenuItem marked>Align Left</BeMenuItem>
			<BeMenuItem>Align Center</BeMenuItem>
			<BeMenuItem>Align Right</BeMenuItem>
			<BeMenuSeparator />
			<BeMenuItem marked>Wrap Lines</BeMenuItem>
		</>
	),
};

export function StyledEditDemo() {
	const [open, setOpen] = useState<string | null>("File");
	const [dropX, setDropX] = useState(0);
	const bar = useRef<HTMLDivElement>(null);

	// A menu drops from ITS item's left edge, and only the engine knows where the
	// flex row put it. Measured here rather than in the kit: #4 owns the real thing.
	useEffect(() => {
		if (open === null) return;
		const i = Object.keys(MENUS).indexOf(open);
		const item = bar.current?.querySelectorAll<HTMLElement>(".be-menu-bar__item")[i];
		if (item) setDropX(item.offsetLeft);
	}, [open]);

	// R5 menus are sticky: Escape or a click outside puts them away.
	useEffect(() => {
		if (open === null) return;
		const away = (e: MouseEvent) => {
			if (!bar.current?.contains(e.target as Node)) setOpen(null);
		};
		const key = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpen(null);
		};
		document.addEventListener("mousedown", away);
		document.addEventListener("keydown", key);
		return () => {
			document.removeEventListener("mousedown", away);
			document.removeEventListener("keydown", key);
		};
	}, [open]);

	return (
		<section id="demo-styled-edit">
			<h2>StyledEdit (BWindow + BMenuBar + BMenu)</h2>
			<p>
				The menu kit in a real window: a BMenuBar at the top of the content, the document under it, and the menu dropping over the text.
				Click a bar item to pull its menu down, Escape or a click away to dismiss. R5 gives a menu a window of its own, so it overlaps the
				document rather than pushing it — and the document is a <code>BTextView</code> inside a <code>BScrollView</code>, which is why the
				scroll view is the well and owns the scrolling.
			</p>
			<BeRow wrap gap="row">
				<BeWindow
					title="Untitled 1"
					onClose={() => {}}
					onZoom={() => {}}
					resizable="knob"
					flush
					minHeight={100}
					contentStyle={{ width: 420, height: 260 }}
				>
					<BeStack fill gap="none">
						<div className="demo-menu-drop" ref={bar}>
							<BeMenuBar aria-label="StyledEdit">
								{Object.keys(MENUS).map((name) => (
									<BeMenuBarItem key={name} selected={open === name} onSelect={() => setOpen(open === name ? null : name)}>
										{name}
									</BeMenuBarItem>
								))}
							</BeMenuBar>

							{/* invoking ANY item puts the menu away, so no item needs its own handler.
							    It waits out the blink, because R5 invokes only once the item stops flashing.
							    A caret item is the exception -- it opens a child rather than invoking. */}
							{open !== null && (
								<BeMenu
									aria-label={open}
									onClick={(e) => {
										if ((e.target as HTMLElement).closest("[aria-haspopup]")) return;
										setTimeout(() => setOpen(null), BE_MENU_INVOKE_MS);
									}}
									style={{ "--demo-drop-x": `${dropX}px` } as CSSProperties}
								>
									{MENUS[open]}
								</BeMenu>
							)}
						</div>

						{/* R5 puts the BTextView INSIDE a BScrollView, so the scroll view IS the well.
						    A contenteditable host (#368) leaves the scrolling to it; a <textarea> could
						    not, since it scrolls its own value and no ancestor can take that over. */}
						<BeScrollView fill horizontal vertical border="fancy">
							<BeStack gap="none" pad="tight">
								{/* biome-ignore lint/a11y/useSemanticElements: a textarea cannot delegate its scrolling -- see above */}
								<div
									className="be-rich-text be-grow"
									contentEditable
									suppressContentEditableWarning
									role="textbox"
									aria-multiline
									aria-label="Document"
									tabIndex={0}
								>
									{SAMPLE.map((line) => (
										<p key={line}>{line}</p>
									))}
								</div>
							</BeStack>
						</BeScrollView>
					</BeStack>
				</BeWindow>
			</BeRow>
		</section>
	);
}
